import { Home, Building2, Car, Siren, Check, Phone, ArrowRight } from "lucide-react";
import { Reveal, SectionHead } from "./Reveal";
import { IMAGES, PHONE_TEL } from "@/site";
import { useQuote } from "@/quote-context";

const SERVICES = [
    {
        id: "residential",
        icon: Home,
        title: "Residential Locksmith",
        tagline: "Protect your home and keep your family secure.",
        img: IMAGES.residential,
        items: [
            "House lockouts",
            "Lock repair and replacement",
            "Rekeying",
            "Lock installation",
            "Deadbolt installation",
            "Door hardware",
            "Key duplication",
            "Smart lock installation",
            "Home security upgrades",
        ],
        cta: "Residential Locksmith Services",
    },
    {
        id: "commercial",
        icon: Building2,
        title: "Commercial Locksmith",
        tagline: "Reliable security solutions for businesses of all sizes.",
        img: IMAGES.commercial,
        items: [
            "Commercial lockouts",
            "Lock repair and replacement",
            "Rekeying",
            "Master key systems",
            "Commercial door hardware",
            "Business security upgrades",
            "High-security locks",
            "Access control solutions",
        ],
        cta: "Commercial Locksmith Services",
    },
    {
        id: "automotive",
        icon: Car,
        title: "Automotive Locksmith",
        tagline: "Locked out of your vehicle or need a replacement key?",
        img: IMAGES.automotive,
        items: [
            "Car lockouts",
            "Car key replacement",
            "Key programming",
            "Transponder keys",
            "Remote / key fob programming",
            "Lost car keys",
            "Ignition-related locksmith services",
            "Vehicle key duplication",
        ],
        cta: "Automotive Locksmith Services",
    },
    {
        id: "emergency",
        icon: Siren,
        title: "Emergency Locksmith",
        tagline: "Locked out? Need help now? Available 24 hours a day, 7 days a week for urgent locksmith situations.",
        img: IMAGES.emergency,
        items: [
            "Home lockouts",
            "Car lockouts",
            "Business lockouts",
            "Broken keys",
            "Lost keys",
            "Lock failures",
            "Emergency lock repair",
            "After-hours locksmith service",
        ],
        cta: "Call for Emergency Service",
    },
];

export const Services = () => {
    const { openQuote } = useQuote();
    const isEmergency = (s) => s.id === "emergency";

    return (
        <section id="services" data-testid="services-section" className="bg-slate-50 py-20 sm:py-24 lg:py-28">
            <div className="mx-auto w-full max-w-7xl px-5 sm:px-8">
                <SectionHead
                    eyebrow="What We Do"
                    title="Every Kind of Locksmith Service"
                    text="Same-day service across Regina and nearby communities — for your home, your business, and your vehicle."
                    testId="services-heading"
                />
                <div className="mt-14 grid gap-6 lg:grid-cols-2">
                    {SERVICES.map((s, i) => (
                        <Reveal key={s.id} delay={(i % 2) * 0.1}>
                            <article
                                data-testid={`service-card-${s.id}`}
                                className={`group flex h-full flex-col overflow-hidden rounded-3xl border transition-[transform,box-shadow] duration-500 hover:-translate-y-1.5 hover:shadow-2xl ${
                                    isEmergency(s)
                                        ? "border-navy-800 bg-navy-950 shadow-navy-950/20 hover:shadow-navy-950/30"
                                        : "border-slate-200 bg-white hover:shadow-navy-950/10"
                                }`}
                            >
                                <div className="relative h-44 overflow-hidden sm:h-52">
                                    <img
                                        src={s.img}
                                        alt={s.title}
                                        loading="lazy"
                                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                                    />
                                    <div
                                        className={`absolute inset-0 ${
                                            isEmergency(s)
                                                ? "bg-gradient-to-t from-navy-950 via-navy-950/40 to-transparent"
                                                : "bg-gradient-to-t from-navy-950/60 via-transparent to-transparent"
                                        }`}
                                    />
                                    <span className="absolute left-5 top-5 flex h-11 w-11 items-center justify-center rounded-xl bg-gold text-navy-950 shadow-lg">
                                        <s.icon className="h-5 w-5" />
                                    </span>
                                </div>
                                <div className="flex flex-1 flex-col p-7">
                                    <h3
                                        className={`font-display text-2xl font-semibold ${
                                            isEmergency(s) ? "text-white" : "text-slate-900"
                                        }`}
                                    >
                                        {s.title}
                                    </h3>
                                    <p className={`mt-2 text-sm leading-relaxed ${isEmergency(s) ? "text-slate-300" : "text-slate-600"}`}>
                                        {s.tagline}
                                    </p>
                                    <ul className="mt-5 grid flex-1 gap-x-4 gap-y-2 sm:grid-cols-2">
                                        {s.items.map((item) => (
                                            <li
                                                key={item}
                                                className={`flex items-start gap-2 text-sm font-medium ${
                                                    isEmergency(s) ? "text-slate-300" : "text-slate-700"
                                                }`}
                                            >
                                                <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                    <div className="mt-7">
                                        {isEmergency(s) ? (
                                            <a
                                                data-testid="service-cta-emergency"
                                                href={PHONE_TEL}
                                                className="inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-gold px-6 py-3.5 text-sm font-extrabold uppercase tracking-wider text-navy-950 transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-gold-light sm:w-auto"
                                            >
                                                <Phone className="h-4 w-4" />
                                                {s.cta}
                                            </a>
                                        ) : (
                                            <button
                                                data-testid={`service-cta-${s.id}`}
                                                onClick={() => openQuote(s.title)}
                                                className="inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-navy-950 px-6 py-3.5 text-sm font-extrabold uppercase tracking-wider text-white transition-[transform,background-color,color] duration-300 hover:-translate-y-0.5 hover:bg-gold hover:text-navy-950 sm:w-auto"
                                            >
                                                {s.cta}
                                                <ArrowRight className="h-4 w-4" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </article>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
};
